/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.dashboard

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.dashboard.DashboardSummary
import studio.oddpods.cauce.core.model.dashboard.PatientStatus
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.SessionType
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.dashboard.generated.resources.Res
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_banner_has_patients
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_banner_no_patients
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_error_load_failed
import kotlin.time.Clock

data class PatientPaymentSummaryDisplay(
    val patientId: String,
    val patientName: String,
    val lastSessionDateDisplay: String?,
    val totalOwedDisplay: String,
    val status: PatientStatus,
)

data class PendingConfirmationDisplay(
    val sessionId: String,
    val patientName: String,
    val sessionDate: String,
    val sessionType: SessionType,
    val paymentStatus: PaymentStatus,
    val feeDisplay: String,
    val amountPaidDisplay: String,
)

data class DashboardState(
    val summary: DashboardSummary? = null,
    val currentMonth: String = "",
    val isLoading: Boolean = true,
    val isRefreshing: Boolean = false,
    val error: UiText? = null,
    val showBanner: Boolean = false,
    val bannerText: UiText? = null,
    val totalCollectedDisplay: String = "",
    val totalOutstandingDisplay: String = "",
    val patientsDisplay: List<PatientPaymentSummaryDisplay> = emptyList(),
    val pendingReminderCount: Int = 0,
    val pendingConfirmations: List<PendingConfirmationDisplay> = emptyList(),
    val savingSessionId: String? = null,
)

sealed class DashboardAction {
    data class MonthChanged(val month: String) : DashboardAction()
    data object Refresh : DashboardAction()
    data object BannerDismissed : DashboardAction()
    data class ConfirmSession(val sessionId: String) : DashboardAction()
    data class CancelSession(val sessionId: String) : DashboardAction()
}

class DashboardViewModel(
    private val dashboardRepository: DashboardRepository,
    private val userDataRepository: UserDataRepository,
    private val patientRepository: PatientRepository,
    private val reminderRepository: ReminderRepository,
    private val sessionRepository: SessionRepository,
    private val currencyFormatter: CurrencyFormatter,
    private val dateFormatter: DateFormatter,
) : BaseViewModel<DashboardState, Unit, DashboardAction>(DashboardState()) {

    init {
        val currentMonth = currentYearMonth()
        mutableStateFlow.value = state.copy(currentMonth = currentMonth)
        loadDashboard(currentMonth)
        loadBanner()
        viewModelScope.launch {
            reminderRepository.getPendingCount().collect { count ->
                mutableStateFlow.value = state.copy(pendingReminderCount = count)
            }
        }
        observePendingConfirmations()
    }

    override fun handleAction(action: DashboardAction) {
        when (action) {
            is DashboardAction.MonthChanged -> {
                mutableStateFlow.value = state.copy(currentMonth = action.month)
                loadDashboard(action.month)
            }

            DashboardAction.Refresh -> loadDashboard(state.currentMonth, isRefresh = true)
            DashboardAction.BannerDismissed -> dismissBanner()
            is DashboardAction.ConfirmSession -> handleConfirmSession(action.sessionId)
            is DashboardAction.CancelSession -> handleCancelSession(action.sessionId)
        }
    }

    private fun loadDashboard(month: String, isRefresh: Boolean = false) {
        mutableStateFlow.value = state.copy(
            isLoading = !isRefresh,
            isRefreshing = isRefresh,
            error = null,
        )
        viewModelScope.launch {
            if (isRefresh) {
                dashboardRepository.refresh()
            }
            combine(
                dashboardRepository.getDashboard(month),
                userDataRepository.observeDisplayPreferences,
            ) { result, _ -> result }
                .collect { result ->
                    result.fold(
                        onSuccess = { summary ->
                            mutableStateFlow.value = state.copy(
                                summary = summary,
                                isLoading = false,
                                isRefreshing = false,
                                totalCollectedDisplay = currencyFormatter.format(summary.totalCollectedThisMonth),
                                totalOutstandingDisplay = currencyFormatter.format(summary.totalOutstanding),
                                patientsDisplay = summary.patients.map { p ->
                                    PatientPaymentSummaryDisplay(
                                        patientId = p.patientId,
                                        patientName = p.patientName,
                                        lastSessionDateDisplay = dateFormatter.format(p.lastSessionDate),
                                        totalOwedDisplay = currencyFormatter.format(p.totalOwed),
                                        status = p.status,
                                    )
                                },
                            )
                        },
                        onFailure = { e ->
                            mutableStateFlow.value = state.copy(
                                isLoading = false,
                                isRefreshing = false,
                                error = e.toUiText(Res.string.dashboard_error_load_failed),
                            )
                        },
                    )
                }
        }
    }

    private fun loadBanner() {
        viewModelScope.launch {
            val dismissed = runCatching {
                userDataRepository.observeOnboardingBannerDismissed.first()
            }.getOrDefault(true)
            if (dismissed) return@launch
            val hasPatients = runCatching {
                patientRepository.getPatients(activeOnly = true).first().isNotEmpty()
            }.getOrDefault(false)
            val text = if (hasPatients) {
                UiText.Resource(Res.string.dashboard_banner_has_patients)
            } else {
                UiText.Resource(Res.string.dashboard_banner_no_patients)
            }
            mutableStateFlow.value = state.copy(showBanner = true, bannerText = text)
        }
    }

    private fun dismissBanner() {
        mutableStateFlow.value = state.copy(showBanner = false, bannerText = null)
        viewModelScope.launch {
            runCatching { userDataRepository.setOnboardingBannerDismissed(true) }
        }
    }

    private fun observePendingConfirmations() {
        viewModelScope.launch {
            combine(
                sessionRepository.getPendingConfirmations(),
                patientRepository.getPatients(activeOnly = true),
            ) { sessions, patients ->
                val patientMap = patients.associateBy { it.id }
                sessions.filter { patientMap.containsKey(it.patientId) }.map { session ->
                    PendingConfirmationDisplay(
                        sessionId = session.id,
                        patientName = patientMap[session.patientId]?.name ?: "",
                        sessionDate = dateFormatter.format(session.sessionDate)
                            ?: session.sessionDate,
                        sessionType = session.sessionType,
                        paymentStatus = session.paymentStatus,
                        feeDisplay = currencyFormatter.format(session.fee),
                        amountPaidDisplay = currencyFormatter.format(session.amountPaid),
                    )
                }
            }.collect { displays ->
                mutableStateFlow.value = state.copy(pendingConfirmations = displays)
            }
        }
    }

    private fun handleConfirmSession(sessionId: String) {
        viewModelScope.launch {
            mutableStateFlow.value = state.copy(savingSessionId = sessionId)
            sessionRepository.confirmSession(sessionId)
                .onSuccess {
                    mutableStateFlow.value = state.copy(savingSessionId = null)
                }.onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        savingSessionId = null,
                        error = e.toUiText(Res.string.dashboard_error_load_failed),
                    )
                }
        }
    }

    private fun handleCancelSession(sessionId: String) {
        viewModelScope.launch {
            mutableStateFlow.value = state.copy(savingSessionId = sessionId)
            sessionRepository.cancelSession(sessionId)
                .onSuccess {
                    mutableStateFlow.value = state.copy(savingSessionId = null)
                }.onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        savingSessionId = null,
                        error = e.toUiText(Res.string.dashboard_error_load_failed),
                    )
                }
        }
    }

    private fun currentYearMonth(): String {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        return "${now.year}-${now.month.number.toString().padStart(2, '0')}"
    }
}
